/**
 * "Manter conectado" da tela de entrada.
 *
 * A sessão do Supabase vai para `localStorage` (sobrevive ao fechamento do
 * navegador) quando a pessoa pede para continuar conectada, e para
 * `sessionStorage` (morre com a aba) quando não pede. A escolha fica gravada
 * antes do sign-in, porque é nesse momento que o cliente decide onde escrever.
 * Leituras consultam os dois lugares, então trocar a escolha não perde uma
 * sessão já aberta; `removeItem` limpa ambos.
 *
 * Navegador sem armazenamento (janela privada com bloqueio) cai em memória e a
 * sessão dura só enquanto a página existir — sem erro, sem demo.
 */
const REMEMBER_KEY = 'cosmos-v2-remember-v1'
const memory = new Map<string, string>()

function storageFor(remember: boolean): Storage | null {
  try {
    return remember ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

export function readRememberSession(): boolean {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) !== 'false'
  } catch {
    return true
  }
}

export function setRememberSession(remember: boolean): void {
  try {
    if (remember) window.localStorage.removeItem(REMEMBER_KEY)
    else window.localStorage.setItem(REMEMBER_KEY, 'false')
  } catch {
    // Sem armazenamento a escolha não persiste; a sessão fica em memória.
  }
}

export const authStorage = {
  getItem(name: string): string | null {
    for (const store of [storageFor(true), storageFor(false)]) {
      try {
        const value = store?.getItem(name)
        if (value != null) return value
      } catch {
        // tenta o próximo lugar
      }
    }
    return memory.get(name) ?? null
  },
  setItem(name: string, value: string): void {
    const remember = readRememberSession()
    const target = storageFor(remember)
    const other = storageFor(!remember)
    try {
      other?.removeItem(name)
    } catch {
      // nada a limpar
    }
    try {
      if (target) {
        target.setItem(name, value)
        return
      }
    } catch {
      // cai em memória abaixo
    }
    memory.set(name, value)
  },
  removeItem(name: string): void {
    for (const store of [storageFor(true), storageFor(false)]) {
      try {
        store?.removeItem(name)
      } catch {
        // já não existe ou está bloqueado
      }
    }
    memory.delete(name)
  },
}
